
import Store from '@/store';
import { IProductList, getPartnerDetailsResult } from '@homeserve/od-typings/api';
import { addressHelper, FetchTreePayload, treeHelper, productListHelper } from '@homeserve/vue-api-plugin';
import WLDiagnosticTree from '@homeserve/vue-components-plugin/src/components/WLDiagnosticTree';
import { breadcrumbHelper, diagnosticHelper } from '@homeserve/vue-components-plugin/src/main';
import GTMPageViewingMixin from '@homeserve/vue-components-plugin/src/mixins/GTMPageViewingMixin';
import { formatPhone } from '@homeserve/vue-components-plugin/src/utils/phoneHelper';
import { Component, Mixins } from 'vue-property-decorator';
import { Getter, State } from 'vuex-class';

@Component({
  components: {
    WLDiagnosticReinsurance: () =>
      import('@homeserve/vue-components-plugin/src/components/WLDiagnostic/WLDiagnosticReinsurance.vue'),
    WLProductList: () => import('@homeserve/vue-components-plugin/src/components/WLHome/WLProductList.vue'),
    WLDiagnosticTree,
  },
})
export default class Diagnostic extends Mixins(GTMPageViewingMixin) {
  private i18n: any;

  @State('domain', { namespace: 'app/diagnostic' })
  private domain!: string;

  @State('tree', { namespace: 'api/tree' })
  private tree: any;

  @Getter('partner')
  private partner!: getPartnerDetailsResult;

  @Getter('productListsForPartner', { namespace: 'api/catalog/productList' })
  private productLists!: IProductList[];

  public async beforeRouteEnter(to, from, next) {
    if (await checkPostCode(to, from, next)) {
      return;
    }

    const treePayload: FetchTreePayload = { domain: to.params.domain };
    if (process.env.VUE_APP_DIAGNOSTIC_TREE) {
      treePayload.id = +process.env.VUE_APP_DIAGNOSTIC_TREE;
    }
    await initContainer(to);
    await treeHelper.resetTree(Store);
    await treeHelper.fetchTree(Store, treePayload);

    if (Store.getters['api/tree/errorFetchingTree']) {
      return next('/404');
    }

    await productListHelper.fetchProductListsForCurrentPartner(Store);

    next();
  }

  public async beforeRouteUpdate(to, from, next) {
    if (await checkPostCode(to, from, next)) {
      return;
    }
    await initContainer(to);
    next();
  }

  get supportPhone(): string {
    return formatPhone(this.partner.supportPhone, '0', true);
  }

  get gtmRouterInfo() {
    return { domain: this.domain };
  }

  private created() {
    this.i18n = {
      header: {
        navNumber: this.$t('navbar.phone_number', {
          phoneNumber: this.supportPhone,
        }),
        navNumberFees: this.$t('navbar.phone_fees'),
        phoneNumberLink: this.$t('navbar.phone_number_link'),
      },
      authFailedTitle: this.$t('authFailed.title'),
      seo: this.getTraductions(this.domain),
    };
  }

  private beforeUpdate() {
    this.i18n.seo = this.getTraductions(this.domain);
  }

  private getTraductions(domain) {
    let i18n: any = {
      diagnosticHelpTitle: this.$t(`diagnosis.${domain}.diagnostic_help_title`),
      diagnosticHelpContent: this.$t(`diagnosis.${domain}.diagnostic_help_content`),
    };

    if (this.$t(`diagnosis.${domain}.product_list_name`) != `diagnosis.${domain}.product_list_name`) {
      i18n.productListName = this.$t(`diagnosis.${domain}.product_list_name`);
    }
    if (this.$t(`diagnosis.${domain}.frequent_repairs_title`) != `diagnosis.${domain}.frequent_repairs_title`) {
      i18n.frequentRepairsTitle = this.$t(`diagnosis.${domain}.frequent_repairs_title`);
      i18n.frequentRepairsContent = this.$t(`diagnosis.${domain}.frequent_repairs_content`);
    }
    if (this.$t(`diagnosis.${domain}.what_to_do_title`) != `diagnosis.${domain}.what_to_do_title`) {
      i18n.whatToDoTitle = this.$t(`diagnosis.${domain}.what_to_do_title`);
      i18n.whatToDoContent = this.$t(`diagnosis.${domain}.what_to_do_content`);
    }
    if (this.$t(`diagnosis.${domain}.troubleshooting_title`) != `diagnosis.${domain}.troubleshooting_title`) {
      i18n.troubleshootingTitle = this.$t(`diagnosis.${domain}.troubleshooting_title`);
      i18n.troubleshootingContent = this.$t(`diagnosis.${domain}.troubleshooting_content`);
    }
    if (this.$t(`diagnosis.${domain}.duration_title`) != `diagnosis.${domain}.duration_title`) {
      i18n.durationTitle = this.$t(`diagnosis.${domain}.duration_title`);
      i18n.durationContent = this.$t(`diagnosis.${domain}.duration_content`);
    }
    if (this.$t(`diagnosis.${domain}.choose_mbp_title`) != `diagnosis.${domain}.choose_mbp_title`) {
      i18n.chooseMbpTitle = this.$t(`diagnosis.${domain}.choose_mbp_title`);
      i18n.chooseMbpContent = this.$t(`diagnosis.${domain}.choose_mbp_content`);
    }
    if (this.$t(`diagnosis.${domain}.owner_or_tenant_title`) != `diagnosis.${domain}.owner_or_tenant_title`) {
      i18n.ownerOrTenantTitle = this.$t(`diagnosis.${domain}.owner_or_tenant_title`);
      i18n.ownerOrTenantContent = this.$t(`diagnosis.${domain}.owner_or_tenant_content`);
    }
    if (this.$t(`diagnosis.${domain}.find_a_pro_title`) != `diagnosis.${domain}.find_a_pro_title`) {
      i18n.findAProTitle = this.$t(`diagnosis.${domain}.find_a_pro_title`);
    }

    if (this.$t(`diagnosis.${domain}.be_helped_out`) != `diagnosis.${domain}.be_helped_out`) {
      i18n.beHelpedOut = this.$t(`diagnosis.${domain}.be_helped_out`);
    }

    return i18n;
  }

  get cityList() {
    const domains = {
      electricite: 'Electricien',
      plomberie: 'Plombier',
    };
    const cities = [
      { name: 'Paris', title: '', link: '' },
      { name: 'Marseille', title: '', link: '' },
      { name: 'Lyon', title: '', link: '' },
      { name: 'Toulouse', title: '', link: '' },
      { name: 'Nice', title: '', link: '' },
      { name: 'Nantes', title: '', link: '' },
      { name: 'Montpellier', title: '', link: '' },
      { name: 'Strasbourg', title: '', link: '' },
      { name: 'Bordeaux', title: '', link: '' },
      { name: 'Lille', title: '', link: '' },
    ];

    if (domains[this.domain]) {
      cities.forEach(city => {
        city.title = domains[this.domain] + ' ' + this.$t(`diagnosis.a`) + ' ' + city.name;
        city.link = '/depannage-domicile/' + city.name.toLowerCase() + '/' + domains[this.domain].toLowerCase() + '/';
      });
    } else {
      cities.length = 0;
    }

    return cities;
  }

  get domainTopProducts() {
    return this.productLists.find(list => list.name === 'diagnostic-' + this.domain);
  }
}

async function checkPostCode(to, from, next) {
  const { postCode } = to.query;
  if (postCode) {
    diagnosticHelper.setPostCode(Store, {
      postCode,
      town: '',
      summary: postCode,
    });
    await addressHelper.setPostCodeSearchResults(Store, [{ postCode, summary: postCode, town: '' }]);

    const { ...rest } = Store.getters.query;

    await Store.dispatch('setQuery', rest);
  }

  const postCodeInStore = diagnosticHelper.getPostCode(Store);

  if ((!postCodeInStore || !postCodeInStore.postCode) && to.params.slug) {
    return next({
      name: 'diagnostic',
      params: { domain: to.params.domain },
      query: { ...to.query },
    });
  }
}

async function initContainer(to) {
  const domain = to.params.domain;
  await diagnosticHelper.setDomain(Store, domain);
  await breadcrumbHelper.setDomain(Store, domain);
}
